import React, { useState } from 'react'
import { FaFileCsv, FaFileExcel, FaChevronDown, FaFileCode, FaRProject } from 'react-icons/fa'
import { Box, Stack, Button, Heading, Text, Icon, Alert, SimpleMenu, ImageIcon } from '@/components/lib'
import api from '../../../api'
import useStore from '../../../store'
import utils from '../../../utils/utils'
import useJob from '../../../hooks/useJob'

import GraphpadImage from '../../../images/graphpad.png'
import SpssImage from '../../../images/spss.png'

function ResultsExport () {
  const [exporting, setExporting] = useState(false)
  const [recordProgress, setRecordProgress] = useState(null)
  const { team, trial, user } = useStore()
  const { startPolling } = useJob()

  function exportStudyRecord () {
    setRecordProgress('Starting')
    api.trials.createStudyRecordJob(trial._id, ({ jobId }) => {
      utils.toast('info', 'Building the study record…', 'You can carry on using Trialflare; it will download when it is ready.')
      startPolling(jobId, {
        onProgress: text => setRecordProgress(text),
        onDone: result => {
          setRecordProgress(null)
          if (!result?.request) return utils.toast('error', 'Unable to download the study record', 'No download link was returned.')
          downloadFile(result.request, result.fileName || `${trial.name.replace(/ /g, '_')}_study_record.zip`)
          utils.toast('success', 'Study record ready', 'Your download should now be in your downloads folder.')
        },
        onError: message => {
          setRecordProgress(null)
          utils.toast('error', 'Unable to build the study record', message)
        }
      })
    }, err => {
      setRecordProgress(null)
      utils.toast('error', err.message)
    })
  }

  function downloadFile (url, fileName) {
    if (!url || !/^(?:https?:|blob:)/.test(url)) {
      utils.toast('error', 'Download not available', 'No valid file URL was provided.')
      return
    }
    const element = document.createElement('a')
    element.setAttribute('href', url)
    element.setAttribute('download', fileName)
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  function exportExcel () {
    setExporting(true)
    const rSuccess = ({ request }) => {
      downloadFile(request, `${trial.name.replace(/ /g, '_')}.xlsx`)
      utils.toast('success', 'Excel file generated successfully', 'It should now be in your downloads folder.')
      setExporting(false)
    }
    const rFail = err => {
      utils.toast('error', err.message)
      setExporting(false)
    }
    api.trials.getResponses(trial._id, 'excel', rSuccess, rFail)
  }

  function exportCSV (dataset) {
    setExporting(true)
    const rSuccess = ({ csv }) => {
      const blob = new Blob([csv ?? ''], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      downloadFile(url, `${trial.name.replace(/ /g, '_')}_${dataset}.csv`)
      setTimeout(() => URL.revokeObjectURL(url), 4000)
      utils.toast('success', 'CSV file generated successfully', 'It should now be in your downloads folder.')
      setExporting(false)
    }
    const rFail = err => {
      utils.toast('error', err.message)
      setExporting(false)
    }

    if (dataset === 'stage') {
      api.trials.getResponses(trial._id, 'csv', rSuccess, rFail)
    } else if (dataset === 'foodDiary') {
      api.trials.getFoodDiaryResponses(trial._id, rSuccess, rFail)
    } else if (dataset.indexOf('hardware') === 0) {
      api.trials.getHardwareResponses(trial._id, 'csv', dataset, rSuccess, rFail)
    }
  }

  // Every statistics-package export follows the same shape: the API builds the
  // file, uploads it and hands back a signed URL.
  function exportStats (type, extension, label) {
    setExporting(true)
    api.trials.getResponses(trial._id, type, ({ request }) => {
      downloadFile(request, `${trial.name.replace(/ /g, '_')}${extension}`)
      utils.toast('success', `${label} file generated successfully`, 'It should now be in your downloads folder.')
      setExporting(false)
    }, err => {
      utils.toast('error', err.message)
      setExporting(false)
    })
  }

  function exportRaw (dataset) {
    setExporting(true)
    if (dataset === 'hardware') {
      // Hardware responses can be large (GB-scale), so the API builds them
      // asynchronously as a job (POST → { jobId }), then the worker uploads a
      // compressed .zip to S3. We poll GET /jobs/<id> until it's done, then
      // download the returned presigned URL — same pattern as the AI
      // stage-import job.
      api.trials.createRawHardwareExportJob(trial._id, ({ jobId }) => {
        utils.toast('info', 'Generating raw hardware archive…', 'You can continue using Trialflare; we\'ll download it automatically when ready.')
        startPolling(jobId, {
          onDone: result => {
            const url = result?.request
            if (!url) {
              utils.toast('error', 'Unable to download raw hardware archive', 'No download link was returned.')
              setExporting(false)
              return
            }
            downloadFile(url, `${trial.name.replace(/ /g, '_')}_raw_hardware.zip`)
            utils.toast('success', 'Raw hardware archive ready', 'Your download should now be in your downloads folder.')
            setExporting(false)
          },
          onError: message => {
            utils.toast('error', 'Unable to export raw hardware data', message)
            setExporting(false)
          }
        })
      }, err => {
        utils.toast('error', err.message)
        setExporting(false)
      })
      return
    }
    // dataset === 'stage' — inline JSON dump (typically small, stays synchronous)
    api.trials.getRawResponses(trial._id, 'stage', data => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      downloadFile(url, `${trial.name.replace(/ /g, '_')}_raw_responses.json`)
      // Give the browser a moment to start the download before revoking the
      // blob URL — revoking synchronously/too early can truncate the saved
      // file on slower machines.
      setTimeout(() => URL.revokeObjectURL(url), 4000)
      utils.toast('success', 'Raw data exported successfully', 'It should now be in your downloads folder.')
      setExporting(false)
    }, err => {
      utils.toast('error', err.message)
      setExporting(false)
    })
  }

  const wearables = trial?.garminEnabled || trial?.fitbitEnabled
  return (
    <Stack gap={5}>
      <Stack direction='row' borderWidth='1px' p={3} gap={5} rounded='md' alignItems='center' bg='bg.canvas'>
        <Stack w={200} alignItems='center' justifyContent='center'>
          <ImageIcon iconKey='download' />
        </Stack>
        <Stack gap={10} flex={1}>
          <Stack>
            <Heading size='md'><Icon as={FaFileCsv} /> Export data as CSV</Heading>
            <Text>CSV files can be imported into spreadsheet apps (such as Excel), stats tools (such as SPSS), loaded into your R or Python programs, or given directly to your data analysts.</Text>
            <Box>
              <SimpleMenu
                trigger={
                  <Button colorPalette='primary'>Download results as CSV <Icon as={FaChevronDown} /></Button>
              } items={[
                { text: 'Core responses', onClick: () => exportCSV('stage') },
                trial?.foodDiariesEnabled ? { text: 'Food diaries', onClick: () => exportCSV('foodDiary') } : null,
                wearables ? { text: 'Wearable daily reports', onClick: () => exportCSV('hardwareDaily') } : null,
                wearables ? { text: 'Wearable sleep reports', onClick: () => exportCSV('hardwareSleep') } : null,
                wearables ? { text: 'Wearable activities', onClick: () => exportCSV('hardwareActivity') } : null
              ]}
              />
            </Box>
          </Stack>

          <Stack>
            <Heading size='md'><Icon as={FaFileExcel} /> Export data in Excel format</Heading>
            <Text>Trialflare can create a workbook file that can be natively imported into Microsoft Excel.</Text>
            <Box><Button colorPalette='primary' onClick={exportExcel}>Download results as an Excel workbook</Button></Box>
          </Stack>

          <Stack>
            <Heading size='md'><Icon as={FaFileCode} /> Export raw database</Heading>
            <Text>Download the database contents in raw JSON format. This is useful where more fine-grained responses are needed or for more in-depth analyses.</Text>
            <Box>
              <SimpleMenu
                trigger={
                  <Button colorPalette='primary' isLoading={exporting}>Download raw export <Icon as={FaChevronDown} /></Button>
              } items={[
                { text: 'Raw stage responses (JSON)', onClick: () => exportRaw('stage') },
                wearables ? { text: 'Raw wearable data (.zip)', onClick: () => exportRaw('hardware') } : null
              ]}
              />
            </Box>
          </Stack>
        </Stack>
      </Stack>

      {utils.canManageTrial(user, trial) &&
        <Stack direction='row' borderWidth='1px' p={3} gap={5} rounded='md' alignItems='center' bg='bg.canvas'>
          <Stack w={200} alignItems='center' justifyContent='center'>
            <ImageIcon iconKey='box' />
          </Stack>
          <Stack flex={1}>
            <Heading size='md'>Study record</Heading>
            <Text>
              Download an archive of the study record.
            </Text>
            <Text fontSize='sm' color='fg.muted'>
              Note that hardware data and eTMF files are not included, as they can be exported separately.
            </Text>
            <Box>
              <Button colorPalette='primary' onClick={exportStudyRecord} loading={!!recordProgress} loadingText={recordProgress || 'Building…'}>
                Build and download the study record
              </Button>
            </Box>
          </Stack>
        </Stack>}

      <Stack direction='row' borderWidth='1px' p={3} gap={5} rounded='md' alignItems='center' bg='bg.canvas'>
        <Box w={200}>
          <img src={SpssImage} />
        </Box>
        <Stack gap={3} flex={1}>
          <Heading size='md'>Export data for SPSS or Stata</Heading>
          <Text>A native data file with your questions as variable labels, choice options as value labels, and numbers, dates and times ready to analyse.</Text>
          <Box>
            {utils.hasFeature(team, 'advancedExport')
              ? (
                <Stack direction='row' gap={3}>
                  <Button colorPalette='primary' isLoading={exporting} onClick={() => exportStats('spss', '.sav', 'SPSS SAV')}>Download for SPSS (.sav)</Button>
                  <Button variant='outline' isLoading={exporting} onClick={() => exportStats('stata', '.dta', 'Stata DTA')}>Download for Stata (.dta)</Button>
                </Stack>
                )
              : <Alert variant='left-accent'>Your team plan does not include this type of export.</Alert>}
          </Box>
        </Stack>
      </Stack>

      <Stack direction='row' borderWidth='1px' p={3} gap={5} rounded='md' alignItems='center' bg='bg.canvas'>
        <Box w={200}>
          <img src={GraphpadImage} />
        </Box>
        <Stack gap={3} flex={1}>
          <Heading size='md'>Export data in GraphPad format</Heading>
          <Text>A Prism project with a data table already laid out for every numeric measure you collect (grouped by study group, and by visit where a stage repeats).</Text>
          <Box>
            {utils.hasFeature(team, 'advancedExport')
              ? <Button colorPalette='primary' isLoading={exporting} onClick={() => exportStats('graphpad', '.pzfx', 'GraphPad Prism PZFX')}>Download results as a PZFX data file</Button>
              : <Alert variant='left-accent'>Your team plan does not include this type of export.</Alert>}
          </Box>
        </Stack>
      </Stack>

      <Stack direction='row' borderWidth='1px' p={3} gap={5} rounded='md' alignItems='center' bg='bg.canvas'>
        <Stack w={200} alignItems='center' justifyContent='center'>
          <ImageIcon iconKey='chart' />
        </Stack>
        <Stack gap={3} flex={1}>
          <Heading size='md'><Icon as={FaRProject} /> Export data for R</Heading>
          <Text>A zip holding your data as a CSV and an R script that reads it in with the right column types, factor levels and labels.</Text>
          <Box>
            {utils.hasFeature(team, 'advancedExport')
              ? <Button colorPalette='primary' isLoading={exporting} onClick={() => exportStats('r', '_R.zip', 'R export')}>Download results for R</Button>
              : <Alert variant='left-accent'>Your team plan does not include this type of export.</Alert>}
          </Box>
        </Stack>
      </Stack>

    </Stack>
  )
}

export default ResultsExport
